import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import ProFeatureGate from '@/components/ProFeatureGate';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, TrendingUp, Trash2, Edit2, BarChart3, Sparkles } from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';
import { fmtMoney, getDisplayCurrency } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

const INVEST_TYPES = ['Stocks', 'Crypto', 'TFSA', 'RRSP', 'Investment', 'Mutual Fund', 'ETF', 'Bonds', 'Real Estate', 'Other'];
const CURRENCIES = ['CAD', 'USD', 'INR', 'GBP', 'EUR', 'AUD'];
const COLORS = ['hsl(136,18%,35%)', 'hsl(43,74%,50%)', 'hsl(197,37%,44%)', 'hsl(280,45%,55%)', 'hsl(30,70%,50%)', 'hsl(8,52%,51%)'];
const fmtCad = (n) => fmtMoney(n);
const fmtCur = (n, cur = 'CAD') => { try { return new Intl.NumberFormat('en-CA', { style: 'currency', currency: cur, minimumFractionDigits: 0 }).format(n || 0); } catch { return `${cur} ${(n || 0).toLocaleString()}`; } };

export default function InvestmentsPage() {
  useDisplayCurrency();
  const [investments, setInvestments] = useState([]);
  const [fxRates, setFxRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [prompts, setPrompts] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: a }, { data: sipData }, { data: prompted }, { data: fxData }] = await Promise.all([
        api.get('/api/networth/assets'),
        api.get('/api/transactions/sip-by-month?months=12').catch(() => ({ data: [] })),
        api.get('/api/transactions?category=Investments&limit=20').catch(() => ({ data: { transactions: [] } })),
        api.get('/api/networth/exchange-rates').catch(() => ({ data: { rates_to_cad: {} } })),
      ]);
      const investTypes = new Set(['Investment', 'TFSA', 'RRSP', 'Stocks', 'Crypto', 'Mutual Fund', 'ETF', 'Bonds']);
      setInvestments(a.filter((x) => investTypes.has(x.asset_type)));
      setFxRates((fxData?.rates_to_cad) || {});
      setMonthlyData(Array.isArray(sipData) ? sipData : []);
      const existingMerchants = new Set(a.filter((x) => investTypes.has(x.asset_type)).map((x) => (x.name || '').toLowerCase()));
      const txns = (prompted.transactions || []).filter((t) => {
        const m = (t.normalized_merchant || t.merchant || '').toLowerCase();
        return m && !existingMerchants.has(m) && !t.linked_asset_id && !t.investment_prompt_dismissed;
      }).slice(0, 5);
      setPrompts(txns);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismissPrompt = async (txnId) => {
    try {
      await api.post(`/api/transactions/${txnId}/dismiss-investment-prompt`);
      setPrompts((prev) => prev.filter((p) => p._id !== txnId));
      toast.success('Dismissed');
    } catch { toast.error('Failed to dismiss'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this investment?')) return;
    try { await api.delete(`/api/networth/assets/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  const totalCad = investments.reduce((s, i) => s + (i.value_cad || 0), 0);

  // Convert CAD amount to user's display currency
  const convertFromCad = (cadAmount) => {
    const displayCur = getDisplayCurrency();
    if (displayCur === 'CAD') return cadAmount;
    const rate = fxRates[displayCur];
    return (rate && rate > 0) ? cadAmount / rate : cadAmount;
  };

  const displaySym = { CAD: 'C$', USD: '$', INR: '₹', GBP: '£', EUR: '€', AUD: 'A$', SGD: 'S$', AED: 'AED', JPY: '¥' }[getDisplayCurrency()] || '$';

  // Pie data — values converted to display currency
  const pieData = {};
  investments.forEach(i => { const t = i.asset_type; pieData[t] = (pieData[t] || 0) + (i.value_cad || 0); });
  const chartData = Object.entries(pieData).map(([name, value]) => ({ name, value: Math.round(convertFromCad(value)) }));

  // Monthly SIP chart — backend returns CAD totals.
  // Convert to user's base currency so mixed-currency SIPs (500 CAD + 100 USD)
  // all show in one consistent unit (e.g. C$642 if base = CAD).
  const monthlyChartData = useMemo(() => {
    return monthlyData
      .map((row) => ({
        label: row.short_label || row.label || row.month,
        fullLabel: row.label || row.month,
        month: row.month,
        amount: Math.round(convertFromCad(row.amount_cad || 0)),
      }))
      .filter((r) => r.amount > 0);
  }, [monthlyData, fxRates]);

  // Total in base currency
  const totalSipAllTime = useMemo(
    () => monthlyData.reduce((s, r) => s + convertFromCad(r.amount_cad || 0), 0),
    [monthlyData, fxRates]
  );
  const totalEffectiveAllTime = totalSipAllTime;

  const pageContent = (
    <div className="space-y-6" data-testid="investments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Investments</h1>
          <p className="text-sm text-muted-foreground mt-1">Track your investment portfolio</p>
        </div>
        <Dialog open={showAdd || !!editItem} onOpenChange={(o) => { if (!o) { setShowAdd(false); setEditItem(null); } else setShowAdd(true); }}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-investment-btn"><Plus className="h-4 w-4 mr-1" />Log Investment</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-['Outfit']">{editItem?._id ? 'Edit Investment' : 'Log Investment'}</DialogTitle></DialogHeader>
            <InvestForm item={editItem} onSave={async () => { await load(); setShowAdd(false); setEditItem(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border border-border shadow-none" data-testid="invest-total-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" /><p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Portfolio ({getDisplayCurrency()})</p></div>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums text-green-600 dark:text-green-400">{fmtMoney(convertFromCad(totalCad))}</p>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1"><BarChart3 className="h-4 w-4 text-primary" /><p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">SIP (12 mo · {getDisplayCurrency()})</p></div>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums text-primary">{fmtMoney(totalSipAllTime)}</p>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Holdings</p>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums mt-1">{investments.length}</p>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">Types</p>
            <p className="text-2xl font-semibold tracking-tight font-['Outfit'] tabular-nums mt-1">{Object.keys(pieData).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Item 14 - Investment prompts banner */}
      {prompts.length > 0 && (
        <Card className="border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/10 shadow-none" data-testid="invest-prompts-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Investment transactions detected</h3>
                <p className="text-xs text-amber-700 dark:text-amber-300/80 mt-0.5">
                  We found {prompts.length} transaction{prompts.length > 1 ? 's' : ''} that look like investments. Add them to your portfolio?
                </p>
                <div className="mt-2 space-y-1">
                  {prompts.slice(0, 3).map((p) => (
                    <div key={p._id} className="flex items-center justify-between text-xs bg-white/60 dark:bg-card/40 rounded px-2 py-1" data-testid={`invest-prompt-${p._id}`}>
                      <div className="truncate flex-1">
                        <span className="font-medium">{p.normalized_merchant || p.merchant}</span>
                        <span className="text-muted-foreground ml-2">{p.date}</span>
                      </div>
                      <div className="flex items-center gap-1.5 ml-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-xs"
                          onClick={() => setEditItem({ name: p.normalized_merchant || p.merchant, asset_type: 'Investment', value: p.amount, currency: p.original_currency || 'CAD', _fromTxn: p._id })}
                          data-testid={`invest-prompt-add-${p._id}`}
                        >
                          Add to Portfolio
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-amber-700 hover:text-red-600 dark:text-amber-300"
                          onClick={() => dismissPrompt(p._id)}
                          data-testid={`invest-prompt-dismiss-${p._id}`}
                          title="Dismiss this suggestion"
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly SIP Investment Chart */}
      <Card className="border border-border shadow-none" data-testid="monthly-building-chart">
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              Monthly SIP / Investments
            </CardTitle>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Invested per month · sourced from transactions · last 12 months
            </p>
          </div>
          {totalSipAllTime > 0 && (
            <div className="text-right shrink-0">
              <p className="text-xs text-muted-foreground">Total invested ({getDisplayCurrency()})</p>
              <p className="text-sm font-semibold tabular-nums text-green-600 dark:text-green-400 font-['Outfit']">
                {fmtMoney(totalSipAllTime)}
              </p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {monthlyChartData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <BarChart3 className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">No investment transactions yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Add a transaction as SIP/Investment type to start tracking monthly contributions.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthlyChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={monthlyChartData.length > 6 ? -35 : 0}
                  textAnchor={monthlyChartData.length > 6 ? 'end' : 'middle'}
                  height={monthlyChartData.length > 6 ? 42 : 20}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${displaySym}${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`}
                  width={44}
                />
                <Tooltip
                  formatter={(value, _name, props) => [fmtMoney(value), `SIP invested — ${props.payload?.fullLabel || ''}`]}
                  contentStyle={{ fontSize: '12px', borderRadius: '8px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
                  itemStyle={{ color: 'hsl(var(--card-foreground))' }}
                  labelStyle={{ display: 'none' }}
                  cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
                />
                <Bar
                  dataKey="amount"
                  name="SIP / Invested"
                  fill="hsl(136,28%,38%)"
                  radius={[4, 4, 2, 2]}
                  maxBarSize={52}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Pie Chart */}
      {chartData.length > 0 && (
        <Card className="border border-border shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" />Portfolio Allocation</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={chartData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" stroke="none" paddingAngle={2}>{chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => fmtCad(v)} contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} itemStyle={{ color: 'hsl(var(--card-foreground))' }} labelStyle={{ color: 'hsl(var(--card-foreground))' }} /></PieChart>
              </ResponsiveContainer>
              <div className="space-y-2">{chartData.map((d, i) => (
                <div key={d.name} className="flex justify-between text-sm"><div className="flex items-center gap-2"><div className="h-3 w-3 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} /><span>{d.name}</span></div><span className="font-semibold tabular-nums">{fmtMoney(d.value)}</span></div>
              ))}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Investment List */}
      <Card className="border border-border shadow-none">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-medium">All Investments</CardTitle></CardHeader>
        <CardContent>
          {loading ? <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-muted rounded animate-pulse" />)}</div>
          : investments.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No investments logged yet. Click &ldquo;Log Investment&rdquo; to add one.</p>
          : (
            <div className="space-y-2">
              {investments.map((inv) => (
                <div key={inv._id} className="flex items-center justify-between p-3 rounded-md border border-border bg-background" data-testid={`invest-${inv._id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0"><TrendingUp className="h-4 w-4 text-primary" /></div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{inv.name}</p>
                      <div className="flex items-center gap-2 mt-0.5"><Badge variant="secondary" className="text-xs">{inv.asset_type}</Badge><Badge variant="outline" className="text-xs">{inv.currency || 'CAD'}</Badge></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <span className="text-sm font-semibold tabular-nums text-green-600 dark:text-green-400">{fmtCur(inv.value, inv.currency || 'CAD')}</span>
                      {inv.currency !== getDisplayCurrency() && inv.value_cad != null && <p className="text-[10px] text-muted-foreground tabular-nums">{fmtMoney(convertFromCad(inv.value_cad), getDisplayCurrency())}</p>}
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditItem(inv)}><Edit2 className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(inv._id)}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <ProFeatureGate feature="Investment Tracking">
      {pageContent}
    </ProFeatureGate>
  );
}

function InvestForm({ item, onSave }) {
  const [form, setForm] = useState({
    name: item?.name || '', asset_type: item?.asset_type || 'Stocks',
    value: item?.value?.toString() || '', currency: item?.currency || 'CAD',
    notes: item?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const isExisting = !!item?._id;  // True existing-asset edit; false for new (incl. txn-prompt prefill)
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, value: parseFloat(form.value) };
      if (isExisting) await api.put(`/api/networth/assets/${item._id}`, payload);
      else await api.post('/api/networth/assets', payload);
      toast.success('Saved'); await onSave();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="space-y-1"><Label className="text-xs">Name</Label><Input data-testid="invest-name-input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g., Wealthsimple TFSA" required /></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Type</Label><Select value={form.asset_type} onValueChange={(v) => set('asset_type', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{INVEST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label className="text-xs">Value</Label><Input data-testid="invest-value-input" type="number" step="0.01" value={form.value} onChange={(e) => set('value', e.target.value)} required /></div>
      </div>
      <div className="space-y-1"><Label className="text-xs">Currency</Label><Select value={form.currency} onValueChange={(v) => set('currency', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
      <div className="space-y-1"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
      <Button type="submit" className="w-full" disabled={saving} data-testid="invest-submit-btn">{saving ? 'Saving...' : item ? 'Update' : 'Log Investment'}</Button>
    </form>
  );
}
